// Campaign link building and validation for the live preview. The backend
// (CampaignLinkService / CampaignLinkUrlBuilder) rebuilds and re-validates every link on
// save with the same rules, so what the user sees here is exactly what gets stored.
//
// Link format (Google Play install referrer):
//   <storeUrl>?id=<package>&referrer=<"utm_source=..&utm_medium=..&.." encoded once more>
// URLSearchParams and Java's URLEncoder both use application/x-www-form-urlencoded
// (space -> "+", only A-Z a-z 0-9 * - . _ left as is), so both sides produce the same string.

// Mirrors the backend CampaignDestination enum.
export const DESTINATIONS = {
  ANDROID_APP: {
    label: "SIA Android app (Google Play)",
    storeUrl: "https://play.google.com/store/apps/details",
    packageId: "com.sia.learn",
  },
};
export const DEFAULT_DESTINATION = "ANDROID_APP";

export const LIMITS = { value: 100, notes: 500, customParams: 20, url: 2000, paramName: 40 };

export const STANDARD_FIELDS = [
  {
    key: "utmSource",
    param: "utm_source",
    label: "Campaign Source",
    required: true,
    placeholder: "e.g. institute_faculty",
    help: "Who or what sends the student: a partner, a platform or a group of people.",
  },
  {
    key: "utmMedium",
    param: "utm_medium",
    label: "Campaign Medium",
    required: true,
    placeholder: "e.g. whatsapp",
    help: "How the link is shared: whatsapp, instagram, email, sms, poster...",
  },
  {
    key: "utmCampaign",
    param: "utm_campaign",
    label: "Campaign Name",
    required: true,
    placeholder: "e.g. faculty_student_invite",
    help: "The name of this promotion. Use the same name everywhere you run it.",
  },
];

export const EMPTY_VALUES = {
  destination: DEFAULT_DESTINATION,
  utmSource: "",
  utmMedium: "",
  utmCampaign: "",
  customParams: [],
  notes: "",
};

const PARAM_NAME = /^[a-z][a-z0-9_]*$/;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;
// "%20", "%3D"... means the value was already URL-encoded and would be encoded twice.
const PERCENT_ENCODED = /%[0-9a-f]{2}/i;

const trim = (v) => (v == null ? "" : String(v).trim());

export const destinationUrl = (destination = DEFAULT_DESTINATION) => {
  const dest = DESTINATIONS[destination] || DESTINATIONS[DEFAULT_DESTINATION];
  const url = new URL(dest.storeUrl);
  url.searchParams.set("id", dest.packageId);
  return url.toString();
};

/** Error message for one parameter value, or "" when it is fine. Empty is checked by the caller. */
export function valueError(label, value) {
  if (value.length > LIMITS.value) return `${label} can be at most ${LIMITS.value} characters`;
  if (CONTROL_CHARS.test(value)) return `${label} contains invalid characters`;
  if (PERCENT_ENCODED.test(value)) {
    return "Looks already URL-encoded (contains %xx). Enter plain text; the link is encoded automatically";
  }
  return "";
}

/** Analytics treats "WhatsApp" and "whatsapp" as different sources: suggest one consistent form. */
export function valueWarning(value) {
  const v = trim(value);
  if (!v) return "";
  const suggestion = v.toLowerCase().replace(/\s+/g, "_");
  return suggestion !== v
    ? `Tip: use lowercase without spaces so reports group it correctly, e.g. "${suggestion}"`
    : "";
}

/** Error message for a custom parameter name, or "". */
export function paramNameError(name) {
  if (name.length > LIMITS.paramName) return `At most ${LIMITS.paramName} characters`;
  if (!PARAM_NAME.test(name)) {
    return "Start with a lowercase letter; use only lowercase letters, numbers and _";
  }
  if (name.startsWith("utm_")) return "Reserved name: use the standard UTM fields above";
  return "";
}

/**
 * Formik validate(): { utmSource: "...", customParams: [{ name, value }], notes, customParamsCount }.
 * Rows that are completely empty are ignored (they are dropped on save).
 */
export function validateCampaign(values) {
  const errors = {};
  STANDARD_FIELDS.forEach((f) => {
    const v = trim(values[f.key]);
    if (!v) {
      if (f.required) errors[f.key] = `${f.label} is required`;
      return;
    }
    const err = valueError(f.label, v);
    if (err) errors[f.key] = err;
  });

  const rows = values.customParams || [];
  const seen = new Map();
  rows.forEach((row) => {
    const name = trim(row.name);
    if (name) seen.set(name, (seen.get(name) || 0) + 1);
  });
  const rowErrors = rows.map((row) => {
    const name = trim(row.name);
    const value = trim(row.value);
    if (!name && !value) return undefined;
    const e = {};
    if (!name) e.name = "Parameter name is required";
    else if (paramNameError(name)) e.name = paramNameError(name);
    else if (seen.get(name) > 1) e.name = "This parameter name is used more than once";
    if (!value) e.value = "Value is required";
    else if (valueError("Value", value)) e.value = valueError("Value", value);
    return Object.keys(e).length ? e : undefined;
  });
  if (rowErrors.some(Boolean)) errors.customParams = rowErrors;
  if (rows.length > LIMITS.customParams) {
    errors.customParamsCount = `At most ${LIMITS.customParams} custom parameters`;
  }
  if (trim(values.notes).length > LIMITS.notes) {
    errors.notes = `Notes can be at most ${LIMITS.notes} characters`;
  }
  if (!Object.keys(errors).length && buildCampaignUrl(values).length > LIMITS.url) {
    errors.url = `The link is longer than ${LIMITS.url} characters. Shorten some values or remove custom parameters`;
  }
  return errors;
}

/** Ordered [name, value] pairs that go into the referrer: filled standard fields, then valid custom rows. */
export function referrerParams(values) {
  const pairs = [];
  STANDARD_FIELDS.forEach((f) => {
    const v = trim(values[f.key]);
    if (v && !valueError(f.label, v)) pairs.push([f.param, v]);
  });
  const used = new Set();
  (values.customParams || []).forEach((row) => {
    const name = trim(row.name);
    const value = trim(row.value);
    if (!name || !value || paramNameError(name) || valueError("Value", value) || used.has(name)) return;
    used.add(name);
    pairs.push([name, value]);
  });
  return pairs;
}

export function buildCampaignUrl(values) {
  const referrer = new URLSearchParams();
  referrerParams(values).forEach(([name, value]) => referrer.append(name, value));
  const url = new URL(destinationUrl(values.destination));
  if (referrer.toString()) url.searchParams.set("referrer", referrer.toString());
  return url.toString();
}

/** Request body for POST/PUT: trimmed values, empty optionals as null, empty custom rows dropped. */
export function toPayload(values) {
  const payload = { destination: values.destination || DEFAULT_DESTINATION };
  STANDARD_FIELDS.forEach((f) => {
    payload[f.key] = trim(values[f.key]) || null;
  });
  payload.customParams = (values.customParams || [])
    .map((row) => ({ name: trim(row.name), value: trim(row.value) }))
    .filter((row) => row.name || row.value);
  payload.notes = trim(values.notes) || null;
  return payload;
}

let rowKey = 0;
export const newParamRow = (name = "", value = "") => ({ key: `p${++rowKey}`, name, value });

/** Form values from a saved campaign (edit, or duplicate when copy = true). */
export function fromCampaign(campaign, { copy = false } = {}) {
  const values = { ...EMPTY_VALUES, destination: campaign.destination || DEFAULT_DESTINATION };
  STANDARD_FIELDS.forEach((f) => {
    values[f.key] = campaign[f.key] || "";
  });
  values.customParams = (campaign.customParams || []).map((p) => newParamRow(p.name, p.value));
  values.notes = copy ? "" : campaign.notes || "";
  return values;
}

/** Copies text, falling back to a hidden textarea where the Clipboard API is unavailable (plain http). */
export async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(area);
  if (!ok) throw new Error("Copy failed");
}

export const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};
